/**
 * The drawing registry (surfaces.md §1.1.2): a drawing id names one client
 * component and declares its state fields (the names a template's
 * `drawing.bind` may fill) and its rungs. The ladder's `L` is read from here.
 *
 * `tools/content_lint.py` still carries a stand-in `DRAWINGS` table
 * (errata E-content-5) with the same one entry; moving the lint onto this
 * module is left to whoever owns the lint.
 *
 * Only `Laipsnis` exists, copied verbatim from ugio_klase (his approved
 * drawing). A drawing id the bundle does not know renders nothing: the words
 * of the beat are still read, and no picture pretends to be the method.
 */
import { useState, type ReactNode } from "react";

import { LaipsnisPriemone, LaipsnisView, asLaipsnis, kartaiOf, laipsnisRungs, lubos } from "./laipsnis";

export interface BeatDrawingProps {
  /** The beat's `focus`: its own numbers, never the variant's. */
  focus: Record<string, unknown> | null;
  /** The drawing state the current step asks her to reach, if it is her turn. */
  target: Record<string, unknown> | null;
  /** Every touch of the drawing, for the explanation's own events. */
  onTouch: (what: string) => void;
}

export interface RungDrawingProps {
  params: Record<string, unknown>;
  rung: number;
  /** Her answer on the variant she missed, for a drawing that draws it back. */
  picked?: string;
  onTouch: (what: string) => void;
}

export interface DrawingDef {
  id: string;
  /** The state fields `drawing.bind` may fill. */
  fields: string[];
  /** How many rungs the ladder has on these params (0: the drawing cannot hold them). */
  rungs(params: Record<string, unknown>): number;
  Beat(p: BeatDrawingProps): ReactNode;
  Rung(p: RungDrawingProps): ReactNode;
}

function laipsnisFocusFromParams(params: Record<string, unknown>) {
  return asLaipsnis({ kind: "laipsnis", laipsniai: [{ pagrindas: params.pagrindas, rodiklis: params.rodiklis }] });
}

function LaipsnisBeat({ focus, target, onTouch }: BeatDrawingProps) {
  const f = asLaipsnis(focus);
  const start = f ? kartaiOf(focus?.kartai, f) : [];
  const [kartai, setKartai] = useState<number[]>(start);
  if (!f) return null;
  const want = target ? kartaiOf((target as { kartai?: unknown }).kartai, f) : null;
  const short = want ? want.findIndex((n, i) => (kartai[i] ?? 1) < n) : -1;
  return (
    <div className="beat-drawing">
      <LaipsnisView
        focus={f}
        kartai={kartai}
        hand={want && short >= 0 ? short : false}
        onPress={
          want
            ? (i) => {
                onTouch(`press:${i}`);
                setKartai((was) => was.map((n, k) => (k === i ? Math.min(n + 1, lubos(f.laipsniai[k].pagrindas)) : n)));
              }
            : undefined
        }
      />
    </div>
  );
}

export const LAIPSNIS: DrawingDef = {
  id: "Laipsnis",
  fields: ["pagrindas", "rodiklis"],
  rungs: (params) => (laipsnisFocusFromParams(params) ? laipsnisRungs(params) : 0),
  Beat: LaipsnisBeat,
  Rung: ({ params, picked, onTouch }) => {
    const focus = laipsnisFocusFromParams(params);
    if (!focus) return null;
    return <LaipsnisPriemone focus={focus} picked={picked} onTouch={() => onTouch("press")} />;
  },
};

export const DRAWINGS: Record<string, DrawingDef> = { Laipsnis: LAIPSNIS };

export function drawingFor(id: string | null | undefined): DrawingDef | undefined {
  return id ? DRAWINGS[id] : undefined;
}
